import React from 'react';
import { 
  Box, 
  Flex, 
  Text, 
  Icon, 
  Heading, 
  Grid,
  GridItem,
  HStack,
  VStack,
  Badge,
  Circle,
  Divider,
  List,
  ListItem,
  ListIcon,
  useColorModeValue
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { 
  CheckCircle, 
  Clock, 
  Users, 
  Server, 
  Shield,
  Code,
  Database
} from 'lucide-react';
import NeuralBackground from '../../components/NeuralBackground';
import SlideContainer from '../../components/SlideContainer';
import { MethodistSlide } from '../../components/base/MethodistSlide';
import JIMILogo from '../../components/JIMILogo';

// Phase card component
const PhaseCard: React.FC<{
  phase: string;
  title: string;
  description: string;
  tasks: string[];
  color: string;
  index: number;
}> = ({ phase, title, description, tasks, color, index }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 * index, duration: 0.5 }}
      style={{ height: "100%" }}
    >
      <Box 
        bg="white"
        borderRadius="lg"
        boxShadow="md"
        overflow="hidden"
        height="100%"
        borderWidth="1px"
        borderColor="gray.200"
        position="relative"
      >
        <Box 
          position="absolute" 
          top={0} 
          left={0} 
          width="8px" 
          height="100%" 
          bg={color} 
        />
        
        <Flex direction="column" height="100%" p={5} pl={6}> 
          <Badge 
            colorScheme={color.replace('#', '').toLowerCase() === '0066cc' ? 'blue' : 'green'} 
            alignSelf="flex-start" 
            mb={2}
          >
            {phase}
          </Badge>
          
          <Text fontWeight="bold" fontSize="lg" color="gray.700" mb={2}> 
            {title}
          </Text>
          
          <Text fontSize="sm" color="gray.600" mb={4}> 
            {description}
          </Text>
          
          <List spacing={2} mt="auto">
            {tasks.map((task, i) => (
              <ListItem key={i} fontSize="sm">
                <ListIcon as={CheckCircle} color={color} />
                {task}
              </ListItem>
            ))}
          </List>
        </Flex>
      </Box>
    </motion.div>
  );
};

const ImplementationSlide: React.FC = () => {
  // Colors
  const methodistBlue = "#0066CC";
  const methodistGreen = "#4A8B71";
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  
  // Implementation phases
  const phases = [
    {
      phase: "Phase 1: Foundation",
      title: "Core Infrastructure Setup",
      description: "Establish the technical foundation and core components of the JIMI platform.",
      tasks: [
        "Set up cloud infrastructure and security protocols",
        "Develop user authentication and profile management",
        "Create basic AI companion framework",
        "Implement data collection mechanisms"
      ],
      color: methodistBlue,
      icon: Server
    },
    {
      phase: "Phase 2: Development",
      title: "Feature Implementation",
      description: "Build and integrate the key features of the platform based on user requirements.",
      tasks: [
        "Develop AI companion conversational capabilities",
        "Create wellness dashboard with metrics visualization",
        "Implement stress management tools",
        "Build community support features"
      ],
      color: methodistGreen,
      icon: Code
    },
    {
      phase: "Phase 3: Testing",
      title: "Quality Assurance",
      description: "Ensure the platform meets all requirements and performs optimally under various conditions.",
      tasks: [
        "Conduct comprehensive security testing",
        "Perform user acceptance testing with clinicians",
        "Optimize performance and responsiveness",
        "Validate AI model accuracy and effectiveness"
      ],
      color: "#805AD5", // Purple
      icon: Shield
    },
    {
      phase: "Phase 4: Deployment",
      title: "Rollout Strategy",
      description: "Strategically deploy the platform to users with proper training and support.",
      tasks: [
        "Implement phased rollout to departments",
        "Provide user training and documentation",
        "Establish support channels and resources",
        "Monitor adoption and gather initial feedback"
      ],
      color: "#DD6B20", // Orange
      icon: Users
    }
  ];
  
  // Implementation timeline
  const timeline = [
    { milestone: "Project Kickoff", duration: "Week 1", status: "Completed" },
    { milestone: "Requirements Finalization", duration: "Weeks 2-3", status: "Completed" },
    { milestone: "Phase 1 Development", duration: "Weeks 4-8", status: "In Progress" },
    { milestone: "Phase 2 Development", duration: "Weeks 9-16", status: "Planned" },
    { milestone: "Testing & QA", duration: "Weeks 17-20", status: "Planned" },
    { milestone: "Pilot Deployment", duration: "Weeks 21-24", status: "Planned" },
    { milestone: "Full Rollout", duration: "Weeks 25-28", status: "Planned" }
  ];
  
  return (
    <MethodistSlide>
      <SlideContainer>
        <Box position="relative" height="100%" overflow="hidden">
          <Box position="absolute" top={0} right={0} bottom={0} left={0} zIndex={-1} opacity={0.05}>
            <NeuralBackground />
          </Box>
          
          <Flex direction="column" height="100%" p={6} maxHeight="100%" overflowY="auto"> 
            {/* Header */}
            <Flex align="center" mb={4}> 
              <JIMILogo height="40px" mr={4} />
              <Heading size="lg">Implementation Strategy</Heading>
            </Flex>
            
            {/* Main content */}
            <Grid templateColumns="2fr 1fr" gap={6} flex="1" maxHeight="calc(100% - 80px)" overflowY="hidden"> 
              {/* Left side - Implementation Phases */}
              <GridItem overflowY="auto"> 
                <VStack spacing={4} align="stretch" height="100%"> 
                  {/* Description */}
                  <Box bg="blue.50" p={3} borderRadius="md" borderWidth="1px" borderColor="blue.100"> 
                    <Text fontSize="sm" color="blue.800">
                      The JIMI Wellness Platform will be implemented using an agile, phased approach to ensure rapid delivery of value
                      while maintaining flexibility to adapt to user feedback and changing requirements.
                    </Text>
                  </Box>
                  
                  {/* Phases grid */}
                  <Grid templateColumns="repeat(2, 1fr)" gap={4} flex="1">
                    {phases.map((phase, index) => (
                      <PhaseCard
                        key={index}
                        phase={phase.phase}
                        title={phase.title}
                        description={phase.description}
                        tasks={phase.tasks}
                        color={phase.color}
                        index={index}
                      />
                    ))}
                  </Grid>
                </VStack>
              </GridItem>
              
              {/* Right side - Timeline */}
              <GridItem overflowY="auto"> 
                <VStack spacing={4} align="stretch" height="100%">
                  <Box bg={bgColor} borderRadius="lg" boxShadow="md" p={4} borderWidth="1px" borderColor={borderColor}>
                    <Heading size="sm" mb={3} color={methodistBlue}> 
                      Implementation Timeline
                    </Heading>
                    
                    <VStack spacing={3} align="stretch">
                      {timeline.map((item, index) => (
                        <Box 
                          key={index} 
                          p={3} 
                          borderRadius="md" 
                          borderWidth="1px" 
                          borderColor="gray.200"
                          bg={
                            item.status === "Completed" ? "green.50" : 
                            item.status === "In Progress" ? "blue.50" : 
                            "gray.50"
                          }
                        >
                          <Flex justify="space-between" mb={1}>
                            <Text fontSize="sm" fontWeight="medium" color="gray.700">
                              {item.milestone}
                            </Text>
                            <Badge 
                              colorScheme={
                                item.status === "Completed" ? "green" : 
                                item.status === "In Progress" ? "blue" : 
                                "gray"
                              }
                              fontSize="xs"
                            >
                              {item.status}
                            </Badge>
                          </Flex>
                          <Flex align="center">
                            <Icon as={Clock} size={14} color="gray.500" mr={1} />
                            <Text fontSize="xs" color="gray.500">
                              {item.duration}
                            </Text>
                          </Flex>
                        </Box>
                      ))}
                    </VStack>
                  </Box>
                  
                  <Box bg="green.50" p={3} borderRadius="md" borderWidth="1px" borderColor="green.100"> 
                    <Heading size="xs" mb={2} color="green.700">
                      Key Success Factors
                    </Heading>
                    <List spacing={2} fontSize="xs" color="green.700">
                      <ListItem>
                        <ListIcon as={CheckCircle} color="green.500" />
                        Continuous stakeholder engagement throughout development
                      </ListItem>
                      <ListItem>
                        <ListIcon as={CheckCircle} color="green.500" />
                        Regular feedback cycles with end-users
                      </ListItem>
                      <ListItem>
                        <ListIcon as={CheckCircle} color="green.500" />
                        Iterative improvement based on usage analytics
                      </ListItem>
                      <ListItem>
                        <ListIcon as={CheckCircle} color="green.500" />
                        Comprehensive training and change management
                      </ListItem>
                    </List>
                  </Box>
                </VStack>
              </GridItem>
            </Grid>
            
            {/* Footer note */}
            <Box mt={3} p={3} bg="blue.50" borderRadius="md" borderWidth="1px" borderColor="blue.100"> 
              <Text fontSize="sm" color="blue.700">
                <Icon as={Database} verticalAlign="middle" mr={1} />
                The implementation plan includes robust data governance and privacy measures to ensure all clinician data
                is protected according to the highest security standards and compliance requirements.
              </Text>
            </Box>
          </Flex>
        </Box>
      </SlideContainer>
    </MethodistSlide>
  );
};

export default ImplementationSlide;
